import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Form,
  Input,
  AutoComplete,
  Space,
  Button,
  InputNumber,
  Select,
  Switch,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import {
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { MenuOutlined } from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false} autoComplete="off">
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableContext = React.createContext(null);

export const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  fieldType,
  required,
  readonly,
  type,
  autocompleteOption,
  modalSelect,
  optionsItems,
  optionsPGforJo,
  childProps,
  cellAlign,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const tdRef = useRef(null);
  const lastValueRef = useRef(undefined);
  const pendingNavRef = useRef(null);
  const isSavingRef = useRef(false);
  const form = useContext(EditableContext);

  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  /* การจัดวางข้อความในช่อง — ค่าเริ่มต้นชิดขวาเหมือนเดิม
     หน้าไหนอยากได้ชิดซ้ายให้ส่ง cellAlign: "start" มาทาง onCell */
  const align = cellAlign || "end";

  type = type || "input";

  useEffect(() => {
    if (editing && !readonly) {
      // preventScroll: ไม่ให้ browser เลื่อนตาราง/หน้าจอตอน focus
      try {
        inputRef.current?.focus({ preventScroll: true });
      } catch {
        inputRef.current?.focus();
      }
      inputRef.current?.select?.();
    }
  }, [editing, readonly]);

  const toggleEdit = (force) => {
    const next = typeof force === "boolean" ? force : !editing;
    if (readonly) return;
    setEditing(next);
    if (next) {
      lastValueRef.current = record[dataIndex];
      const rawVal = record[dataIndex];
      const fieldVal =
        type === "date" && rawVal ? dayjs(rawVal) : rawVal;
      form.setFieldsValue({ [dataIndex]: fieldVal });
    }
  };

  const buildRowIdentity = () => {
    const identity = {};
    ["seq", "id", "_rid", "stcode","pg_code"].forEach((field) => {
      if (record?.[field] !== undefined) {
        identity[field] = record[field];
      }
    });
    return identity;
  };

  const buildSavePayload = (values = {}) => ({
    ...buildRowIdentity(),
    ...values,
    key: dataIndex,
  });

  const save = async () => {
    if (isSavingRef.current) return;
    try {
      isSavingRef.current = true;
      const values = await form.validateFields([dataIndex]);
      setEditing(false);
      handleSave(buildSavePayload(values));
      if (pendingNavRef.current) {
        const dir = pendingNavRef.current;
        pendingNavRef.current = null;
        setTimeout(() => moveToNeighbor(dir), 0);
      }
    } catch {}
    finally {
      setTimeout(() => {
        isSavingRef.current = false;
      }, 0);
    }
  };

  const select = async () => {
    if (isSavingRef.current) return;
    try {
      isSavingRef.current = true;
      const values = await form.validateFields([dataIndex]);
      setEditing(false);
      handleSave(buildSavePayload(values));
      // If navigation not already scheduled by keydown, schedule it
      if (!pendingNavRef.current) {
        pendingNavRef.current = "right";
      }
      setTimeout(() => {
        const dir = pendingNavRef.current;
        pendingNavRef.current = null;
        moveToNeighbor(dir);
      }, 0);
    } catch {}
    finally {
      setTimeout(() => {
        isSavingRef.current = false;
      }, 0);
    }
  };

  const selectPGforJo = async (data, v) => {
    try {
      setEditing(false);

      handleSave({
        ...buildRowIdentity(),
        _rid: record._rid,
        pg_code: v.pg_code,
        pg_name: v.pg_name,
        code: v.code,
        pg_stname_cus: v.pg_stname_cus,
        fg_name: v.fg_name,
        unit_cs: v.unit_cs,
        price: v.price,
        carton: 1,
      });
      // After updating row data, focus price editor in same row
      setTimeout(() => {
        focusColInSameRow("carton");
      }, 0);
    } catch {}
  };

  const switced = async () => {
    try {
      const values = await form.validateFields([dataIndex]);
      handleSave(buildSavePayload(values));
    } catch {}
  };

  const findEditableTdByKey = (rowEl, colKey, fallbackIndex) => {
    if (!rowEl) return null;
    if (colKey) {
      const m = Array.from(rowEl.children).find(
        (c) => c.getAttribute && c.getAttribute("data-col") === colKey
      );
      if (m && m.getAttribute("data-editable") === "1") return m;
    }
    const td = rowEl.children[fallbackIndex];
    if (td && td.getAttribute("data-editable") === "1") return td;
    return Array.from(rowEl.children).find(
      (c) => c.getAttribute && c.getAttribute("data-editable") === "1"
    );
  };

  const findHorizontal = (startTd, stepKey) => {
    let cur = startTd;
    while (cur && cur[stepKey]) {
      cur = cur[stepKey];
      if (cur.getAttribute && cur.getAttribute("data-editable") === "1")
        return cur;
    }
    return null;
  };
  const findFirstEditableInRow = (rowEl) => {
    if (!rowEl) return null;
    return Array.from(rowEl.children).find(
      (c) => c.getAttribute && c.getAttribute("data-editable") === "1"
    );
  };

  const moveToNeighbor = (dir) => {
    const td = tdRef.current;
    if (!td) return;
    const tr = td.closest("tr");
    if (!tr) return;
    const colKey = td.getAttribute("data-col");
    const colIndex = Array.from(tr.children).indexOf(td);

    let targetTd = null;
    if (dir === "down") {
      const nextTr = tr.nextElementSibling;
      targetTd = findEditableTdByKey(nextTr, colKey, colIndex);
    } else if (dir === "right") {
      // try same row to the right
      targetTd = findHorizontal(td, "nextElementSibling");
      // if none, jump to first editable cell of next row
      if (!targetTd) {
        const nextTr = tr.nextElementSibling;
        targetTd = findFirstEditableInRow(nextTr);
      }
    }

    if (targetTd) {
      const clickable = targetTd.querySelector(".editable-cell-value-wrap");
      const el = clickable || targetTd;
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
      el.click();
      setTimeout(() => focusEditor(targetTd), 0);
    }
  };

  const handleWrapperKeyDown = (e) => {
    if (!editable || readonly) return;
    if (!editing) {
      if (e.key === "Enter") {
        e.preventDefault();
        toggleEdit(true);
        setTimeout(() => focusEditor(tdRef.current), 0);
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        moveToNeighbor("right");
        return;
      }
    }
  };

  const focusColInSameRow = (colKey) => {
    let attempt = 0;
    const MAX = 12;
    const tryFocus = () => {
      const baseTd = tdRef.current;
      const tr = baseTd?.closest("tr");
      const targetTd = tr?.querySelector(`td[data-col='${colKey}']`);
      if (targetTd && targetTd.getAttribute("data-editable") === "1") {
        const clickable = targetTd.querySelector(".editable-cell-value-wrap");
        (clickable || targetTd).click();
        requestAnimationFrame(() => focusEditor(targetTd));
      } else if (attempt < MAX) {
        attempt++;
        requestAnimationFrame(tryFocus);
      }
    };
    requestAnimationFrame(tryFocus);
  };

  const handleEditorKeyDown = (e) => {
    if (!editable || readonly) return;
    if (type === "modal-select") {
      if (e.key === "Enter") {
        e.preventDefault();
        setEditing(false);
        modalSelect?.(e, record);
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        pendingNavRef.current = "right";
        setEditing(false);
        moveToNeighbor("right");
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        form.setFieldsValue({ [dataIndex]: lastValueRef.current });
        setEditing(false);
      }
      return;
    }
    // Treat Enter and Tab identically: move right (or next row first cell)
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pendingNavRef.current = "right";
      save();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      form.setFieldsValue({ [dataIndex]: lastValueRef.current });
      setEditing(false);
    }
  };

  let childNode = children;
  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
          // ล็อกความสูงให้เท่าโหมดแสดงผล แถวจะได้ไม่ขยับตอนเข้า/ออกโหมดแก้ไข
          height: !!fieldType ? 64 : 32,
          boxSizing: "border-box",
        }}
        name={dataIndex}
        rules={[{ required: !!required, message: `${title} is required.` }]}
      >
        <>
          {type === "input" && (
            <Input
              placeholder="Enter value"
              ref={inputRef}
              onKeyDown={handleEditorKeyDown}
              onBlur={save}
              style={{
                height: 32,
                width: "100%",
                minWidth: 0,
                textAlign: align,
                ...restProps.style,
              }}
              className="ant-input"
              autoComplete="off"
              readOnly={readonly}
            />
          )}
          {type === "textarea" && (
            <textarea
              rows={2}
              ref={inputRef}
              onBlur={save}
              onKeyDown={handleEditorKeyDown}
              style={{ width: "100%", height: 64 }}
              className="ant-input"
              readOnly={readonly}
            />
          )}
          {type === "autocomplete" && (
            <AutoComplete
              children={
                <Input
                  ref={inputRef}
                  style={{ height: 32, ...restProps.style }}
                  onKeyDown={handleEditorKeyDown}
                />
              }
              onBlur={save}
              onSelect={save}
              options={autocompleteOption}
              placeholder="Enter value"
              filterOption={(inputValue, option) =>
                option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                -1
              }
            />
          )}
          {type === "modal-select" && (
            <Space.Compact
              style={{ width: "100%", height: 32, minWidth: "none" }}
            >
              <Input
                readOnly
                placeholder="Choose value"
                value={record[dataIndex]}
                style={{ height: 32, ...restProps.style }}
                onKeyDown={handleEditorKeyDown}
              />
              <Button
                className="bn-secondary bn-center bn-action"
                size="small"
                icon={<SearchOutlined />}
                onClick={(e) => modalSelect(e, record)}
                style={{
                  minWidth: 40,
                  height: 32,
                  borderRadius: 6,
                  borderStartStartRadius: 0,
                  borderEndStartRadius: 0,
                }}
              />
            </Space.Compact>
          )}
          {type === "number" && (
            <InputNumber
              placeholder="กรอกข้อมูล"
              ref={inputRef}
              onKeyDown={(e) => {
                if (childProps?.onKeyDown) childProps.onKeyDown(e);
                handleEditorKeyDown(e);
              }}
              onBlur={save}
              style={{
                height: 32,
                width: "100%",
                minWidth: 0,
                textAlign: align,
                ...restProps.style,
              }}
              className={`width-100 input-30 ${align === "end" ? "!text-end" : "!text-start"}`}
              autoComplete="off"
              readOnly={readonly}
              controls={false}
              {...childProps}
            />
          )}
          {type === "switch" && (
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              onChange={switced}
              onKeyDown={handleEditorKeyDown}
            />
          )}
          {type === "select" && (
            <Select
              allowClear
              autoClearSearchValue={false}
              showSearch
              style={{ width: "100%" }}
              className="input-30"
              placeholder="เลือกข้อมูล"
              options={(optionsItems || []).map((m) => ({
                label: m.label,
                value: m.value,
              }))}
              filterOption={filterOption}
              onSelect={select}
              onBlur={save}
              onKeyDown={handleEditorKeyDown}
              onInputKeyDown={handleEditorKeyDown}
              disabled={readonly}
            />
          )}
          {type === "date" && (
            <DatePicker
              style={{ width: "100%", height: 32 }}
              format={childProps?.format || "DD/MM/YYYY"}
              onChange={(date) => {
                const strVal = date ? date.format("YYYY-MM-DD") : null;
                setEditing(false);
                handleSave(buildSavePayload({ [dataIndex]: strVal }));
              }}
            />
          )}
          {type === "select-pg-jo" && (
            <Select
              allowClear
              autoClearSearchValue={false}
              showSearch
              style={{ width: "100%" }}
              className="input-30"
              placeholder="เลือกข้อมูล"
              options={(optionsPGforJo || []).map((m) => ({
                label: m.label,
                value: m.value,
                code: m.code,
                pg_name: m.label,
                pg_code: m.value,
                fg_name: m.fg_name,
                unit_cs: m.unit_cs,
                price: m.price,
              }))}
              filterOption={filterOption}
              onSelect={(data, value) => selectPGforJo(data, value)}
              onBlur={save}
              disabled={readonly}
            />
          )}
        </>
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        tabIndex={0}
        style={{
          boxSizing: "border-box",
          width: "100%",
          paddingTop: 0,
          paddingBottom: 0,
          paddingRight: 11,
          paddingLeft: 11,
          textAlign: align,
          height: !!fieldType ? 64 : 32,
          border: "1px solid #eee",
          borderRadius: 6,
          lineHeight: !!fieldType ? "1.4rem" : "30px",
          outline: "none",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        onClick={() => toggleEdit(true)}
        onKeyDown={handleWrapperKeyDown}
      >
        {children}
      </div>
    );
  }

  return (
    <td
      {...restProps}
      ref={tdRef}
      data-col={dataIndex}
      data-editable={editable ? "1" : "0"}
      tabIndex={editable ? 0 : -1}
    >
      {childNode}
    </td>
  );
};

export const RowDrag = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });
  const [form] = Form.useForm();
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      }
    ),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 999,
        }
      : {}),
  };
  return (
    <Form form={form} component={false} autoComplete="off">
      <EditableContext.Provider value={form}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes}>
          {React.Children.map(children, (child) => {
            if (child.key === "sort") {
              return React.cloneElement(child, {
                children: (
                  <MenuOutlined
                    ref={setActivatorNodeRef}
                    style={{
                      touchAction: "none",
                      cursor: "move",
                    }}
                    {...listeners}
                  />
                ),
              });
            }
            return child;
          })}
        </tr>
      </EditableContext.Provider>
    </Form>
  );
};

const focusEditor = (cellEl) => {
  if (!cellEl) return;
  const el =
    cellEl.querySelector("input, textarea") ||
    cellEl.querySelector(".ant-input-number input") ||
    cellEl.querySelector(".ant-select-selector input") ||
    cellEl.querySelector(".ant-select-selection-search-input");

  if (el) {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
    try {
      el.select?.();
    } catch {}
    return;
  }

  // Fallback: open Select to expose its input, then focus it
  const sel = cellEl.querySelector(".ant-select");
  if (sel) {
    sel.querySelector(".ant-select-selector")?.click();
    const sInput =
      sel.querySelector(".ant-select-selector input") ||
      sel.querySelector(".ant-select-selection-search-input");
    try {
      sInput?.focus({ preventScroll: true });
    } catch {
      sInput?.focus();
    }
  }
};
